import { dirname, join } from 'node:path';
import fsSync from 'node:fs';
import type { Agent, Message, Service } from './types';
import matter from 'gray-matter';
import { getResource, getResourcePath, isLatestVersion } from './internal/resources';
import { findFileById, getFiles } from './internal/utils';
import { getServices } from './services';
import { getAgents } from './agents';
import { satisfies, validRange } from 'semver';

type MessageParticipant = Service | Agent;

/**
 * Returns a message from EventCatalog by a given schema path.
 *
 * @example
 * ```ts
 * import utils from '@eventcatalog/utils';
 *
 * const { getMessageBySchemaPath } = utils('/path/to/eventcatalog');
 *
 * // Get the message by the schema path
 * const message = await getMessageBySchemaPath('/path/to/eventcatalog/messages/InventoryAdjusted/schema.json');
 * const message = await getMessageBySchemaPath('/path/to/eventcatalog/messages/InventoryAdjusted/schema.avro');
 * ```
 */
export const getMessageBySchemaPath =
  (directory: string) =>
  async (path: string, options?: { attachSchema?: boolean }): Promise<Message> => {
    const pathToMessage = dirname(path);
    try {
      const files = await getFiles(`${directory}/${pathToMessage}/index.{md,mdx}`);

      if (!files || files.length === 0) {
        throw new Error(`No message definition file (index.md or index.mdx) found in directory: ${pathToMessage}`);
      }
      const messageFile = files[0];

      const { data } = matter.read(messageFile);
      const { id, version } = data;

      if (!id || !version) {
        throw new Error(`Message definition file at ${messageFile} is missing 'id' or 'version' in its frontmatter.`);
      }

      const message = await getResource(directory, id, version, { type: 'message', ...options });

      if (!message) {
        throw new Error(`Message resource with id '${id}' and version '${version}' not found, as referenced in ${messageFile}.`);
      }
      return message as Message;
    } catch (error) {
      // console.error(`Failed to get message for schema path ${path}. Error processing directory ${pathToMessage}:`, error);
      if (error instanceof Error) {
        // Prepend more context to the existing error message
        error.message = `Failed to retrieve message from ${pathToMessage}: ${error.message}`;
        throw error;
      }
      throw new Error(`Failed to retrieve message from ${pathToMessage} due to an unknown error.`);
    }
  };

/**
 * Returns the producers and consumers (services and agents) for a given message.
 *
 * @example
 * ```ts
 * import utils from '@eventcatalog/utils';
 *
 * const { getProducersAndConsumersForMessage } = utils('/path/to/eventcatalog');
 *
 * // Returns the producers and consumers (services and agents) for a given message
 * const { producers, consumers } = await getProducersAndConsumersForMessage('InventoryAdjusted', '0.0.1');
 */
export const getProducersAndConsumersForMessage =
  (directory: string) =>
  async (
    id: string,
    version?: string,
    options?: { latestOnly?: boolean }
  ): Promise<{ producers: MessageParticipant[]; consumers: MessageParticipant[] }> => {
    const [services = [], agents = []] = await Promise.all([
      getServices(directory)({ latestOnly: options?.latestOnly ?? true }),
      getAgents(directory)({ latestOnly: options?.latestOnly ?? true }),
    ]);
    const message = (await getResource(directory, id, version, { type: 'message' })) as Message;
    const isMessageLatestVersion = await isLatestVersion(directory, id, version);

    if (!message) {
      throw new Error(`Message resource with id '${id}' and version '${version}' not found.`);
    }

    const producers: MessageParticipant[] = [];
    const consumers: MessageParticipant[] = [];

    for (const participant of [...services, ...agents]) {
      const participantPublishesMessage = participant.sends?.some((_message) => {
        if (_message.version) {
          const isParticipantUsingSemverRange = validRange(_message.version);
          if (isParticipantUsingSemverRange) {
            return _message.id === message.id && satisfies(message.version, _message.version);
          } else {
            return _message.id === message.id && message.version === _message.version;
          }
        }
        if (isMessageLatestVersion && _message.id === message.id) {
          return true;
        }
        return false;
      });
      const participantSubscribesToMessage = participant.receives?.some((_message) => {
        if (_message.version) {
          const isParticipantUsingSemverRange = validRange(_message.version);
          if (isParticipantUsingSemverRange) {
            return _message.id === message.id && satisfies(message.version, _message.version);
          } else {
            return _message.id === message.id && message.version === _message.version;
          }
        }
        if (isMessageLatestVersion && _message.id === message.id) {
          return true;
        }
        return false;
      });

      if (participantPublishesMessage) {
        producers.push(participant);
      }
      if (participantSubscribesToMessage) {
        consumers.push(participant);
      }
    }

    return { producers, consumers };
  };

/**
 * Returns the consumers of a given schema path.
 *
 * @example
 * ```ts
 * import utils from '@eventcatalog/utils';
 *
 * const { getConsumersOfSchema } = utils('/path/to/eventcatalog');
 *
 * // Returns the consumers of a given schema path
 * const consumers = await getConsumersOfSchema('events/InventoryAdjusted/schema.json');
 */
export const getConsumersOfSchema = (directory: string) => async (path: string) => {
  try {
    const message = await getMessageBySchemaPath(directory)(path);
    const { consumers } = await getProducersAndConsumersForMessage(directory)(message.id, message.version);
    return consumers;
  } catch (error) {
    return [];
  }
};

/**
 * Returns the producers of a given schema path.
 *
 * @example
 * ```ts
 * import utils from '@eventcatalog/utils';
 *
 * const { getProducersOfSchema } = utils('/path/to/eventcatalog');
 *
 * // Returns the producers of a given schema path
 * const producers = await getProducersOfSchema('events/InventoryAdjusted/schema.json');
 */
export const getProducersOfSchema = (directory: string) => async (path: string) => {
  try {
    const message = await getMessageBySchemaPath(directory)(path);
    const { producers } = await getProducersAndConsumersForMessage(directory)(message.id, message.version);
    return producers;
  } catch (error) {
    return [];
  }
};

/**
 * Returns the schema for a given message (event, command or query) by its id and version.
 *
 * If no version is given, the latest version is used.
 *
 * @example
 * ```ts
 * import utils from '@eventcatalog/utils';
 *
 * const { getSchemaForMessage } = utils('/path/to/eventcatalog');
 *
 * // Get the schema for the latest version of the message
 * const schema = await getSchemaForMessage('InventoryAdjusted');
 *
 * // Get the schema for a specific version
 * const schema = await getSchemaForMessage('InventoryAdjusted', '0.0.1');
 * ```
 */
export const getSchemaForMessage =
  (directory: string) =>
  async (id: string, version?: string): Promise<{ schema: string; fileName: string } | undefined> => {
    const file = await findFileById(directory, id, version);
    if (!file || !fsSync.existsSync(file)) return undefined;

    const { data } = matter.read(file);

    if (!data.schemaPath) return undefined;

    const resourceDirectory = dirname(file);
    const pathToSchema = join(resourceDirectory, data.schemaPath);

    if (!fsSync.existsSync(pathToSchema)) return undefined;

    const schema = fsSync.readFileSync(pathToSchema, 'utf8');

    return {
      schema,
      fileName: data.schemaPath,
    };
  };
